import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { sortBy, groupBy } from 'lodash';
import { Selection } from 'src/domains/sportsbook/webview/components/Selection';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { ExpandButton } from 'src/domains/sportsbook/webview/components/expandButton/ExpandButton';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { sortSelectionsByCriteria } from 'src_common/utils/sport/sort';
import {
    getFootballSelectionSortWeight,
    isFootballBusinessOrderMarket,
    sortFootballMarketSelections,
} from 'src_common/utils/sport/sortFootballMarketSelections';
import {
    sortOverUnderSelectionGroups,
    sortOverUnderSelections,
} from 'src/domains/sportsbook/webview/components/marketGroups/lineGroupedMarketGroup/sortOverUnderColumnNames';
import {
    Empty,
    MarketLoader,
    MarketLoaderWrapper,
    SelectionGroupListVersion,
    SelectionHeader,
    SelectionsGroup,
    SelectionsGroupLiItem,
    SelectionsGroupList,
    SelectionsGroupName,
    SelectionsGroupWrap,
    SelectionsGroupWrapper,
} from 'src/domains/sportsbook/webview/components/selectionsGroupUnified/SelectionGroupUnified.style';
import { useCommon } from 'src/domains/common/Common';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import classNames from 'classnames';
import { isCustomMarket } from 'src/domains/sportsbook/utils/isCustomMarket';
import { isOutrightEventItem } from 'src/domains/sportsbook/utils/isOutrightEvent';

const SELECTION_ORDER: Record<string, Array<string>> = {
    'race-to': ['A', 'N', 'H'],
    'both-teams-to-score': ['N', 'Y'],
    'away-to-win-to-nil': ['N', 'Y'],
    'home-to-win-to-nil': ['N', 'Y'],
    'home-clean-sheet': ['N', 'Y'],
    'away-clean-sheet': ['N', 'Y'],
    'red-card-in-match': ['N', 'Y'],
    overtime: ['N', 'Y'],
    'will-runs-be-scored-by-first-ball': ['N', 'Y'],
    'first-over-runs-odd-even': ['E', 'O'],
    'double-chance': ['H-A', 'A-D', 'H-D'],
    'time-of-1st-goal-over-under': ['0-30', 'NG', '31-90'],
    'time-of-2nd-goal-over-under': ['0-50', 'NG', '51-90'],
    'time-of-3rd-goal-over-under': ['0-70', 'NG', '71-90'],
    'fight-end': ['D', 'KO', 'PNTS'],
    'total-frames': ['U', 'Ext', 'O'],
    'match-result-and-goals-over-under': ['H', 'D', 'A'],
    default: ['A', 'D', 'H'],
};

const MULTIPLES = [
    // 'correct-score',
    // 'half-time-full-time',
    // 'half-time-correct-score',
    // match-result-and-goals-over-under: keep as separate cells (H-U, H-O, ...)
    // so 6 headers align with 6 odds — grouping by H/D/A stacks 2 odds under 2 labels.
    /^set-correct-score-[1-9]$/,
    /number-of-goals/,
    /-bands/, // to cover all bands markets
];

const ONE_COLUMN = [
    /match[-\s]goals/i,
    'half-time-full-time',
    'match-result-and-both-to-score',
    /^(?!.*under\/over\/no[-\s]goal$)time[-\s]of[-\s][1-9][a-z]{2}.*goal$/i,
    /time[-\s]of[-\s]first.+goal$/i,
    /total goals( \(reduced\)){0,}$/i,
    /\d[a-z]{2}[-\s]half[-\s]goals$/,
    /tryscorer/i,
    // /match winner/i,
];

const STACK_ON_MOBILE = [/number-of-goals/, /-bands/];

const COLUMNS: Record<string, number> = {
    'one-column': 1,
    'two-column': 2,
    'three-column': 3,
};

const test = (market: string): ((id: string | RegExp) => boolean) => {
    return (id: string | RegExp): boolean => {
        if (id instanceof RegExp) {
            return id.test(market);
        } else {
            return id === market;
        }
    };
};

const getTemplateId = (selection: SelectionModel): string => {
    const marketTemplateId = selection.templateMarketId;
    // Get market display template and if is 1-3 column then create a single group
    const marketDisplayTemplate = selection.marketDisplayTemplate;
    // Use selection template when no identifier was found
    let templateId = selection.identifier ?? selection.templateId;
    // Create selections groups based on what display template was specified
    // otherwise fallback to default behavior
    if (templateId === undefined) {
        return '';
    }

    if (marketDisplayTemplate === undefined) {
        return templateId;
    }

    if (marketDisplayTemplate !== '' && Object.keys(COLUMNS).includes(marketDisplayTemplate)) {
        return 'single';
    }

    if (marketDisplayTemplate !== '' && marketDisplayTemplate !== '-') {
        return templateId.charAt(0);
    }

    if (templateId === 'T' || templateId === '??') {
        //TODO: temporary solution for anyOtherScore templateId ='??' -> should be fixed on the BE
        templateId = 'D';
    }

    if (marketTemplateId === undefined) {
        return templateId;
    }
    if (templateId === 'unnamed-favourite' && marketTemplateId !== 'race-winner') {
        return 'player';
    } // TODO: remove after new-trading release -> temporary solution for old trading - New Trading will Fix That

    if (marketTemplateId !== '' && MULTIPLES.some(test(marketTemplateId))) {
        return templateId.charAt(0);
    }

    // Neither/Both share templateId "custom" — use distinct group keys so they render as separate cells.
    if (/which-teams?-to-score/i.test(marketTemplateId)) {
        const id = templateId.toUpperCase();
        if (id === 'H' || id === 'A') {
            return id;
        }
        const name = selection.name.toLowerCase();
        if (name.includes('neither')) {
            return 'neither';
        }
        if (name.includes('both')) {
            return 'both';
        }
    }

    if (marketTemplateId !== '' && ONE_COLUMN.some(test(marketTemplateId))) {
        return 'a';
    }

    return templateId;
};

const getOrder = (selection: SelectionModel): number => {
    const marketTemplateId = selection.templateMarketId;
    if (marketTemplateId === undefined) {
        return 0; //recordGet(SELECTION_ORDER, 'default'); in case of problems uncomment
    }

    const list = SELECTION_ORDER[marketTemplateId] ?? SELECTION_ORDER.default ?? [];
    return list.indexOf(getTemplateId(selection));
};
//eslint-disable-next-line
const groupSelections = (selections: Array<SelectionModel>) => {
    let selectionsArrayNew: SelectionModel[] = [];
    for (const selection of selections) {
        selectionsArrayNew.push(selection);

        selectionsArrayNew.filter(
            (sel: SelectionModel) =>
                !(
                    sel.marketDisplayTemplate !== undefined &&
                    //eslint-disable-next-line
                    sel.marketDisplayTemplate.match(/home-away/) &&
                    sel.identifier !== undefined &&
                    //eslint-disable-next-line
                    sel.identifier.match(/D/gi)
                )
        );

        const useFootballBusinessOrder = selectionsArrayNew.some((item) =>
            isFootballBusinessOrderMarket(item.templateMarketId)
        );

        if (useFootballBusinessOrder) {
            // Keep business order — do not re-sort by displayOrder / getOrder (scrambled on BetGenius).
            selectionsArrayNew = sortBy(selectionsArrayNew, (selection) => getFootballSelectionSortWeight(selection));
        } else {
            selectionsArrayNew = sortBy(selectionsArrayNew, (selection) => selection.displayOrder);
            selectionsArrayNew = sortBy(
                selectionsArrayNew,
                (selection) => selection.marketDisplayOrder !== undefined && -selection.marketDisplayOrder
            );
            selectionsArrayNew = sortBy(selectionsArrayNew, (selection) => -getOrder(selection));
        }
    }

    // // Group selections by template and keep the order of the groups found
    return groupBy(selectionsArrayNew, getTemplateId);
};

const selectionsByPropsNew = (selections: Record<string, SelectionModel[]>): SelectionModel[] => {
    const selectionsList: Record<number, SelectionModel[]> = {};

    for (const key of Object.keys(selections)) {
        const singleSelection = selections[key] ?? [];
        //@ts-expect-error
        selectionsList[key] = singleSelection.map((selection: SelectionModel | null) => {
            //eslint-disable-next-line
            return (selection = selection && selection.id2.getModel());
        });
    }

    const selectionsListValues = Object.values(selectionsList);
    return selectionsListValues[0] ?? [];
};

interface SelectionGroupItemPropsType {
    selection: SelectionModel;
}

const SelectionGroupItem = observer('SelectionGroupItem', ({ selection }: SelectionGroupItemPropsType) => {
    const common = useCommon();
    const languagesState = LanguagesState.get(common);
    const selectionName = languagesState.getSelectionTranslationName(selection);

    return (
        <SelectionsGroupLiItem>
            <SelectionsGroupName title={selectionName}>
                <span>{selectionName}</span>
            </SelectionsGroupName>

            <Selection selectionId={selection.id2} />
        </SelectionsGroupLiItem>
    );
});

interface SelectionGroupPropsType {
    isHomepage?: boolean;
    eventId: EventId | null;
    marketIdMain: MarketId;
    selections: Record<string, SelectionModel[]>;
}

export const SelectionGroupUnified = observer('SelectionGroupUnified', (props: SelectionGroupPropsType) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const common = useCommon();
    const languagesState = LanguagesState.get(common);

    const { eventId, marketIdMain, isHomepage } = props;
    const eventModel = eventId === null ? null : eventId.getEventModel();

    const getSelectionsGroup = (
        selections: SelectionModel[],
        limitRowToDisplay: number,
        key: string,
        isLarge = false
    ): JSX.Element | null => {
        const selectionsToRender = isExpanded ? selections : selections.slice(0, limitRowToDisplay);

        if (eventModel === null) {
            return null;
        }

        return (
            <React.Fragment key={key}>
                {selectionsToRender.map((selection) =>
                    isLarge ? (
                        <SelectionGroupItem
                            key={selection.id}
                            selection={selection}
                        />
                    ) : (
                        <SelectionsGroupList
                            key={selection.id}
                            className='selections-group-list'
                        >
                            <SelectionGroupItem selection={selection} />
                        </SelectionsGroupList>
                    )
                )}
            </React.Fragment>
        );
    };

    const toggleExpand = (e: React.MouseEvent): void => {
        e.preventDefault();
        setIsExpanded(!isExpanded);
    };

    const limitRowToDisplay = 4;
    const limitColumnToDisplay = 3;
    const eventMarket: MarketModel | null = marketIdMain.getModel();
    const selections = sortFootballMarketSelections(selectionsByPropsNew(props.selections));
    const selectionsGroup = groupSelections(selections);
    const selectionSize = selections.length;
    const selectionsGroupValuesList = Object.values(selectionsGroup);
    const selectionsGroupListValue = selectionsGroupValuesList[0];
    const templateMarketId = eventMarket?.templateId ?? '';
    const isLoading = eventMarket === null;
    const eventMarketName = !isLoading ? eventMarket.name : '';

    const marketDisplayTemplate = eventMarket?.displayTemplate;
    const isOutright = isOutrightEventItem(eventModel);
    const isCustomLayout = isCustomMarket(templateMarketId) || isOutright;

    const isOneColumn =
        (selectionSize > limitColumnToDisplay && MULTIPLES.some(test(templateMarketId)) === false) ||
        ONE_COLUMN.some(test(templateMarketId)) ||
        ONE_COLUMN.some(test(eventMarketName));

    const columnNumber = COLUMNS[marketDisplayTemplate ?? ''] ?? 1;
    const columns = isOneColumn && !isCustomLayout ? 1 : columnNumber;

    const limitDisplayBasedOnColumns = (selectionsGroupListValue?.length ?? 0) / columns;

    // Get group with most selections used for expandButton
    // if there are more then the limit to display
    const selectionsGroupKeysList = Object.keys(selectionsGroup);
    const selectionsGroupListKeysItem = selectionsGroupKeysList[0];
    const biggestGroup =
        selectionsGroupListKeysItem === 'single'
            ? limitDisplayBasedOnColumns
            : selectionsGroupValuesList.reduce(
                  (biggest: number, items: SelectionModel[]) => (biggest < items.length ? items.length : biggest),
                  0
              );

    const selectionsToRender = (() => {
        const entries = Object.entries(selectionsGroup);

        if (isFootballBusinessOrderMarket(templateMarketId)) {
            // Object.entries reorders integer-like keys ("4", "7") ahead of "0-1"/"2-3".
            // Re-sort groups by business weight so bands stay ascending.
            return [...entries].sort(([, groupA], [, groupB]) => {
                const weightA = groupA[0] !== undefined ? getFootballSelectionSortWeight(groupA[0]) : 0;
                const weightB = groupB[0] !== undefined ? getFootballSelectionSortWeight(groupB[0]) : 0;
                return weightA - weightB;
            });
        }

        // Classic Over/Under: feed displayOrder often puts Under first — force Over → Under.
        return sortOverUnderSelectionGroups(entries);
    })();

    const sectionClass = {
        'selection-group--columns': isOneColumn || isCustomLayout,
        'selection-group--stack-on-mobile':
            STACK_ON_MOBILE.some(test(templateMarketId)) && selectionsToRender.length > 1,
    };

    const itShouldDisplayExpandButton = biggestGroup > limitRowToDisplay;

    const sortedSelectionsToRender: Array<[string, SelectionModel[]]> = selectionsToRender.map(
        ([key, itemsIn]: [string, SelectionModel[]]) => {
            const selection = itemsIn[0];
            if (selection === undefined) {
                return [key, itemsIn];
            }

            const displayOrder = selection.marketSelectionOrdering;
            const order = displayOrder !== undefined && displayOrder !== '-' ? displayOrder : 'by-display-order';
            const items = isFootballBusinessOrderMarket(selection.templateMarketId)
                ? sortFootballMarketSelections(itemsIn)
                : sortOverUnderSelections(sortSelectionsByCriteria(itemsIn, order));

            return [key, items];
        }
    );

    const selectionsLabels = sortedSelectionsToRender
        .map(([, items]) => items.map((selection) => languagesState.getSelectionTranslationName(selection)))
        .flat();

    const sortedSelectionsToRenderSize = sortedSelectionsToRender.length;
    const showSelectionHeader = !isOneColumn && !isCustomLayout;

    return (
        <SelectionsGroupWrap>
            <SelectionsGroup className={classNames('selections-group', sectionClass)}>
                <Empty
                    className='empty'
                    selectionsCount={sortedSelectionsToRenderSize}
                >
                    {sortedSelectionsToRenderSize === 0 ? (
                        <MarketLoaderWrapper>{isLoading ? <MarketLoader version='dark' /> : null}</MarketLoaderWrapper>
                    ) : (
                        <>
                            {showSelectionHeader ? <SelectionHeader selections={selectionsLabels} /> : null}
                            <SelectionsGroupWrapper>
                                {sortedSelectionsToRender.map(([key, items]: [string, SelectionModel[]]) => {
                                    if (items.length === 0) {
                                        return null;
                                    }

                                    if (selectionSize <= limitRowToDisplay && !isCustomLayout) {
                                        return getSelectionsGroup(items, limitRowToDisplay, key);
                                    }

                                    const columnRows =
                                        eventMarket !== null && isExpanded
                                            ? selectionSize
                                            : limitRowToDisplay * columns;

                                    return (
                                        <SelectionGroupListVersion
                                            columns={columns}
                                            key={key}
                                        >
                                            {getSelectionsGroup(items, columnRows, key, true)}
                                        </SelectionGroupListVersion>
                                    );
                                })}
                            </SelectionsGroupWrapper>
                        </>
                    )}
                </Empty>
            </SelectionsGroup>
            {itShouldDisplayExpandButton ? (
                <ExpandButton
                    isExpanded={isExpanded}
                    toggleExpand={toggleExpand}
                    isHomepage={isHomepage}
                />
            ) : undefined}
        </SelectionsGroupWrap>
    );
});
